/**
 * How long the console's realtime socket waits before it tries again.
 *
 * The socket used to retry ONCE, a second later, and only after a connection
 * it had already made dropped. A FIRST connection that failed — `/socket-token`
 * answering 500 while the API restarted, or not answering at all — was never
 * retried: the error escaped `onMounted` (Sentry, 18 customers in a week) and
 * the page lived on the polling fallback until it was reloaded.
 *
 * Every retry now goes through this one schedule: capped exponential backoff,
 * so an API that is down for minutes is asked a handful of times rather than
 * every second, and jitter, so every console that lost the API in the same
 * deploy does not come back in the same instant. The delay is never under half
 * of its ceiling, which keeps the first retry close to a second — what a
 * momentary blip deserves. The attempt count goes back to zero on `connect`.
 */
export const SOCKET_RECONNECT_BASE_MS = 1_000
export const SOCKET_RECONNECT_MAX_MS = 30_000

/** Delay before retry number `attempt` (0 for the first retry after a failure). */
export function socketReconnectDelayMs(attempt: number, random: () => number = Math.random): number {
  const step = Number.isFinite(attempt) && attempt > 0 ? Math.floor(attempt) : 0
  const ceiling = Math.min(SOCKET_RECONNECT_MAX_MS, SOCKET_RECONNECT_BASE_MS * 2 ** step)
  const fraction = Math.min(Math.max(random(), 0), 1)
  return Math.round(ceiling / 2 + (ceiling / 2) * fraction)
}
